import { Toast } from 'primereact/toast';
import { useState, useEffect, useRef } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Button } from 'primereact/button';
import { fetchAccessToken } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { getRowClassName } from '../utils/common';

export const VwImport = () => {
    const { getAccessTokenSilently } = useAuth0();

    const [imports, setImports] = useState([]);
    const [leftSelection, setLeftSelection] = useState(null);
    const [rightSelection, setRightSelection] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogContent, setDialogContent] = useState("");

    const toast = useRef(null);
    const compareCacheRef = useRef({});

    const showError = (err) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: err,
                life: 3000
            });
        }
    }

    useEffect(() => {
      document.title = 'OptiConfig VWFS Imports';

        async function loadImports() {
            setIsRunning(true);
            try {
                const token = await fetchAccessToken(getAccessTokenSilently);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/imports`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    }
                });

                const responseObj = await response.json();

                if (response.status === 500) {
                    console.error("ERROR!", responseObj.message, responseObj.details);
                    throw responseObj;
                }

                if (responseObj.error) {
                    showError(responseObj.error);
                }

                setImports(responseObj);

            } catch (e) {
                console.error(e);
                showError(e.details || e.message);
            }
            finally {
                setIsRunning(false);
            }
        }

        loadImports();
    }, [getAccessTokenSilently]);

    const formatDate = (dateStr) => moment.utc(dateStr).local().format('DD.MM.YY HH:mm:ss');

    const filteredRight = leftSelection
        ? imports.filter(i => i.brand === leftSelection.brand && i.importSessionId !== leftSelection.importSessionId)
        : [];

    async function getTableData(changeState, currImportSessionId, prevImportSessionId) {
        const cacheKey = `${currImportSessionId}::${prevImportSessionId}`;
        if (!compareCacheRef.current[cacheKey]) {
            compareCacheRef.current[cacheKey] = {};
        }
        const sessionCache = compareCacheRef.current[cacheKey];

        async function getForState(cs) {
            if (sessionCache[cs]) {
                return sessionCache[cs];
            }
            const data = await callCompare(currImportSessionId, prevImportSessionId, cs);
            sessionCache[cs] = data;
            return data;
        }

        if (Array.isArray(changeState)) {
            const results = await Promise.all(changeState.map((cs) => getForState(cs)));
            return results.flat().filter(Boolean);
        }
        return await getForState(changeState);
    }

    function ChangeStateTable({ changeState, currImportSessionId, prevImportSessionId }) {
        const [rows, setRows] = useState([]);
        const [loading, setLoading] = useState(true);
        const [first, setFirst] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(20);

        useEffect(() => {
            getTableData(changeState, currImportSessionId, prevImportSessionId).then((data) => {
                setRows(data);
                setLoading(false);
            });
        }, [changeState, currImportSessionId, prevImportSessionId]);

        const exportToExcel = () => {
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
            saveAs(blob, 'export.xlsx');
        };

        const header = (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                    type="button"
                    icon="pi pi-file-excel"
                    label="Export"
                    onClick={exportToExcel}
                    className="p-button-success"
                />
            </div>
        );

        return (
            <>
                {loading && <ProgressSpinner />}
                {!loading && <DataTable
                    value={rows}
                    paginator
                    rows={rowsPerPage}
                    first={first}
                    onPage={(e) => {
                        setFirst(e.first);
                        setRowsPerPage(e.rows);
                    }}
                    paginatorPosition="top"
                    paginatorRight
                    responsiveLayout="scroll"
                    header={header}
                    scrollable
                    scrollHeight="60vh"
                    filterDisplay="row"
                    className={'no-filter-column'}
                    style={{
                        fontSize: '0.9em',
                        padding: 0,
                        margin: 0,
                        lineHeight: 1.2,
                        minWidth: 'max-content',
                    }}
                    showGridlines
                    rowClassName={getRowClassName(rows)}
                >
                    {rows.length > 0 &&
                        Object.keys(rows[0]).map((key) => (
                            <Column
                                key={key}
                                field={key}
                                header={
                                    <span
                                        style={{
                                            padding: '2px 6px',
                                            fontSize: '0.95em',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: 'block',
                                        }}
                                        title={key}
                                    >
                                        {key.charAt(0).toUpperCase() + key.slice(1)}
                                    </span>
                                }
                                style={{
                                    minWidth: 100,
                                    padding: '2px 4px',
                                }}
                                headerStyle={{
                                    padding: '2px 4px',
                                }}
                                filter
                                sortable
                                filterPlaceholder={`Filter by ${key}`}
                                filterMatchMode="contains"
                                showFilterMenu={false}
                                showClearButton={false}
                            />
                        ))}
                </DataTable>}
            </>
        );
    }

    function renderTabContent(changeState, currImportSessionId, prevImportSessionId) {
        return (
            <div
                style={{
                    height: '70vh',
                    padding: 0,
                    margin: 0,
                    position: 'relative',
                    background: 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '0.9em',
                }}
            >
                <div
                    style={{
                        flex: 1,
                        width: '100%',
                        height: '100%',
                        overflowX: 'auto',
                        overflowY: 'auto',
                    }}
                >
                    <ChangeStateTable changeState={changeState} currImportSessionId={currImportSessionId} prevImportSessionId={prevImportSessionId} />
                </div>
            </div>
        );
    }

    function dialogComponent(compareRespObj, currImportSessionId, prevImportSessionId) {
        const combinedStates = ['new', 'equal'];
        const allCount = compareRespObj
            .filter((r) => combinedStates.includes(r.changeState))
            .reduce((sum, r) => sum + (r.recordCount || 0), 0);

        return (
            <TabView activeIndex={1}>
                <TabPanel key="all" header={`all (from session): ${allCount}`}>
                    {renderTabContent(combinedStates, currImportSessionId, prevImportSessionId)}
                </TabPanel>
                {compareRespObj.map((r) => (
                    <TabPanel key={r.changeState} header={`${r.changeState}: ${r.recordCount}`}>
                        {renderTabContent(r.changeState, currImportSessionId, prevImportSessionId)}
                    </TabPanel>
                ))}
            </TabView>
        );
    }

    function showComparison(compareRespObj, currImportSessionId, prevImportSessionId) {
        setDialogContent(dialogComponent(compareRespObj, currImportSessionId, prevImportSessionId));
        setDialogVisible(true);
    }

    async function callCompare(curr, prev, state) {
        if (curr && prev) {
            // toast.current.show({
            //     severity: 'info',
            //     summary: 'Comparing Imports',
            //     detail: `Left: ${curr.importSessionId}, Right: ${prev.importSessionId}`,
            // });

            setIsRunning(true);

            try {
                let url = `${process.env.REACT_APP_API_URL}/imports/compare/${curr}/${prev}`;

                if (state)
                    url = `${url}/${state}`;

                const token = await fetchAccessToken(getAccessTokenSilently);

                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    }
                });

                const responseObj = await response.json();

                if (response.status === 500) {
                    console.error("ERROR!", responseObj.message, responseObj.details);
                    throw responseObj;
                }

                if (responseObj.error) {
                    showError(responseObj.error);
                }

                return responseObj;
            }
            catch (e) {
                console.error(e);
                showError(e.details || e.message || "API call failed");
            }
            finally {
                setIsRunning(false);
            }
        }
    }

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="grid" style={{ maxWidth: '95em' }}>
                <div className="col">
                    <h4>&nbsp;Import session</h4>
                    <DataTable
                        value={imports}
                        selection={leftSelection}
                        onSelectionChange={(e) => {
                            setLeftSelection(e.value);
                            setRightSelection(null);
                        }}
                        selectionMode="single"
                        dataKey="importSessionId"
                        responsiveLayout="scroll"
                        className={'no-filter-column'}
                        filterDisplay="row"
                        sortField="importDate"         // <-- default sort field
                        sortOrder={-1}                // <-- -1 for descending
                    >
                        <Column
                            field="brand"
                            header="Brand"
                            filter
                            sortable
                            filterMatchMode="contains"
                            filterPlaceholder="Filter by Brand"
                            showFilterMenu={false}
                            showClearButton={false}
                        />
                        <Column
                            field="importDate"
                            header="Import Date"
                            body={rowData => formatDate(rowData.importDate)}
                            filter={false}
                            sortable
                        />
                        <Column
                            field="recordCount"
                            header="Records"
                            filter={false}
                            sortable
                            filterMatchMode="contains"
                            filterPlaceholder="Filter by Records"
                            showFilterMenu={false}
                            showClearButton={false}
                        />
                    </DataTable>
                </div>

                <div className="col">
                    {leftSelection && <h4>&nbsp;Compare with</h4>}
                    {!leftSelection && <h4>&nbsp;Please select one import section</h4>}
                    {leftSelection && <DataTable
                        value={filteredRight}
                        selection={rightSelection}
                        onSelectionChange={async (e) => {
                            if (!e.value) return;
                            setRightSelection(e.value);
                            const compResult = await callCompare(leftSelection.importSessionId, e.value.importSessionId);

                            showComparison(compResult, leftSelection.importSessionId, e.value.importSessionId);
                        }}
                        selectionMode="single"
                        dataKey="importSessionId"
                        responsiveLayout="scroll"
                        sortField="importDate"      // default sort field
                        sortOrder={-1}              // descending
                    >
                        <Column
                            field="brand"
                            header="Brand"
                            sortable
                        />
                        <Column
                            field="importDate"
                            header="Import Date"
                            body={rowData => formatDate(rowData.importDate)}
                            sortable
                        />
                        <Column
                            field="recordCount"
                            header="Records"
                            sortable
                        />
                    </DataTable>}
                    {isRunning && <ProgressSpinner />}
                </div>
            </div>
            <Dialog header="Difference analysis"
                visible={dialogVisible}
                style={{ width: '90vw', minHeight: '95vh' }}
                onHide={() => { if (!dialogVisible) return; setDialogVisible(false); }}>
                {dialogContent}
            </Dialog>
        </div>
    );
};